/* ==========================================================================
   Maison — theme scripts (vanilla JS, custom elements)
   ========================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};

  /* ---------- Utilities ---------- */
  const debounce = (fn, wait = 300) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(null, args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const jsonConfig = (body) => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body)
  });

  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

  const toast = (message) => {
    const el = document.querySelector('[data-toast]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('is-visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('is-visible'), 3000);
  };

  const lockScroll = () => document.body.classList.add('is-locked');
  const unlockScroll = () => {
    if (!document.querySelector('.drawer.is-open')) document.body.classList.remove('is-locked');
  };

  /* ---------- Reveal on scroll ---------- */
  let revealObserver;
  const initReveal = (root = document) => {
    if (!document.body.classList.contains('has-reveal')) return;
    const items = root.querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const siblings = el.parentElement ? [...el.parentElement.children].filter((c) => c.hasAttribute('data-reveal')) : [];
          const index = Math.max(0, siblings.indexOf(el));
          el.style.transitionDelay = `${Math.min(index, 6) * 80}ms`;
          el.classList.add('is-revealed');
          revealObserver.unobserve(el);
        });
      }, { rootMargin: '0px 0px -60px 0px', threshold: 0.05 });
    }
    items.forEach((el) => revealObserver.observe(el));
  };

  /* ---------- Drawer base ---------- */
  class Drawer extends HTMLElement {
    connectedCallback() {
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    open(opener) {
      if (this.classList.contains('is-open')) return;
      document.querySelectorAll('.drawer.is-open').forEach((d) => d !== this && d.close && d.close(true));
      this.opener = opener || document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      lockScroll();
      document.addEventListener('keydown', this.onKeyDown);
      requestAnimationFrame(() => {
        const target = this.querySelector('[data-autofocus]') || this.querySelector('input[type="search"]') || this.querySelector(focusableSelector);
        target?.focus({ preventScroll: true });
      });
    }

    close(silent) {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.removeEventListener('keydown', this.onKeyDown);
      unlockScroll();
      if (!silent) this.opener?.focus?.({ preventScroll: true });
    }

    onKeyDown(event) {
      if (event.key === 'Escape') this.close();
      if (event.key !== 'Tab') return;
      const focusables = [...this.querySelectorAll(focusableSelector)].filter((el) => el.offsetParent !== null);
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }
  }

  class MenuDrawer extends Drawer {}
  class FacetDrawer extends Drawer {}

  /* ---------- Cart drawer ---------- */
  class CartDrawer extends Drawer {
    renderSection(html) {
      if (!html) return;
      const doc = parseHTML(html);
      const fresh = doc.querySelector('cart-drawer');
      if (!fresh) return;
      this.querySelector('.drawer__inner').innerHTML = fresh.querySelector('.drawer__inner').innerHTML;
      updateCartCount(fresh.dataset.cartCountValue);
    }
  }

  const updateCartCount = (count) => {
    if (count === undefined || count === null) return;
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', Number(count) === 0);
    });
  };

  const getCartDrawer = () => document.querySelector('cart-drawer');

  const cartSections = () => {
    const sections = ['cart-drawer'];
    const main = document.querySelector('[data-main-cart]');
    if (main) sections.push(main.dataset.mainCart);
    return sections;
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    getCartDrawer()?.renderSection(sections['cart-drawer']);
    const main = document.querySelector('[data-main-cart]');
    if (main && sections[main.dataset.mainCart]) {
      const fresh = parseHTML(sections[main.dataset.mainCart]).querySelector('[data-main-cart]');
      if (fresh) main.replaceWith(fresh);
    }
  };

  const changeLine = async (line, quantity, itemEl) => {
    itemEl?.classList.add('is-loading');
    try {
      const res = await fetch(routes.cartChange, jsonConfig({
        line,
        quantity,
        sections: cartSections(),
        sections_url: window.location.pathname
      }));
      const data = await res.json();
      if (data.errors || data.status) {
        toast(data.errors || data.description || theme.strings.error);
        itemEl?.classList.remove('is-loading');
        return;
      }
      renderCartSections(data.sections);
      updateCartCount(data.item_count);
    } catch (e) {
      toast(theme.strings.error);
      itemEl?.classList.remove('is-loading');
    }
  };

  document.addEventListener('change', debounce((event) => {
    const input = event.target.closest?.('[data-line-quantity]');
    if (!input) return;
    changeLine(Number(input.dataset.lineQuantity), Math.max(0, parseInt(input.value, 10) || 0), input.closest('.cart-item'));
  }, 350));

  document.addEventListener('change', (event) => {
    const note = event.target.closest?.('[data-cart-note]');
    if (!note) return;
    fetch(routes.cartUpdate, jsonConfig({ note: note.value }));
  });

  document.addEventListener('click', (event) => {
    const remove = event.target.closest('[data-line-remove]');
    if (!remove) return;
    event.preventDefault();
    changeLine(Number(remove.dataset.lineRemove), 0, remove.closest('.cart-item'));
  });

  /* ---------- Add to cart ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form || theme.cartType !== 'drawer' || !getCartDrawer()) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const errorEl = form.querySelector('[data-form-error]');
    if (button?.disabled) return;
    button?.classList.add('is-loading');
    if (errorEl) errorEl.hidden = true;

    const formData = new FormData(form);
    formData.append('sections', 'cart-drawer');
    formData.append('sections_url', window.location.pathname);

    try {
      const res = await fetch(routes.cartAdd, {
        method: 'POST',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/javascript' },
        body: formData
      });
      const data = await res.json();
      if (data.status) {
        const message = data.description || data.message || theme.strings.error;
        if (errorEl) { errorEl.textContent = message; errorEl.hidden = false; }
        else toast(message);
        return;
      }
      getCartDrawer().renderSection(data.sections && data.sections['cart-drawer']);
      quickView.close();
      getCartDrawer().open(button);
    } catch (e) {
      toast(theme.strings.error);
    } finally {
      button?.classList.remove('is-loading');
    }
  });

  /* ---------- Search drawer + predictive search ---------- */
  class SearchDrawer extends Drawer {
    connectedCallback() {
      super.connectedCallback();
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.input.hasAttribute('data-predictive')) return;
      this.input.addEventListener('input', debounce(() => this.search(), 300));
    }

    async search() {
      const q = this.input.value.trim();
      if (!q) {
        this.results.innerHTML = '';
        this.classList.remove('has-results');
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const url = `${routes.predictiveSearch}?q=${encodeURIComponent(q)}&section_id=predictive-search` +
        '&resources[type]=product,collection,article,page,query&resources[limit]=6&resources[options][unavailable_products]=last';
      try {
        const res = await fetch(url, { signal: this.controller.signal });
        if (!res.ok) return;
        const doc = parseHTML(await res.text());
        const content = doc.querySelector('[data-predictive-search-results]');
        this.results.innerHTML = content ? content.outerHTML : '';
        this.classList.toggle('has-results', Boolean(content));
      } catch (e) { /* aborted */ }
    }
  }

  /* ---------- Global open/close delegation ---------- */
  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer && typeof drawer.open === 'function') {
        event.preventDefault();
        drawer.open(opener);
        return;
      }
    }
    const closer = event.target.closest('[data-drawer-close]');
    if (closer) {
      const drawer = closer.closest('.drawer');
      if (drawer && closer.tagName !== 'A') event.preventDefault();
      drawer?.close();
      return;
    }
    // Clicking a drawer's own backdrop (the drawer element itself, outside .drawer__inner) closes it
    if (event.target.classList?.contains('drawer') && event.target.classList.contains('is-open')) {
      event.target.close();
      return;
    }

    const modalOpener = event.target.closest('[data-modal-open]');
    if (modalOpener) {
      const dialog = document.getElementById(modalOpener.dataset.modalOpen);
      if (dialog?.showModal) { dialog.showModal(); lockScroll(); }
    }
    const modalCloser = event.target.closest('[data-modal-close]');
    if (modalCloser) {
      modalCloser.closest('dialog')?.close();
    }
    if (event.target.tagName === 'DIALOG' && event.target.classList.contains('modal')) {
      event.target.close();
    }

    const quick = event.target.closest('[data-quick-view]');
    if (quick) {
      event.preventDefault();
      quickView.open(quick.dataset.quickView, quick);
    }
  });

  document.addEventListener('close', (event) => {
    if (event.target.tagName === 'DIALOG') unlockScroll();
  }, true);

  /* ---------- Quick view ---------- */
  const quickView = {
    dialog: null,
    ensure() {
      if (this.dialog) return this.dialog;
      const dialog = document.createElement('dialog');
      dialog.className = 'modal quick-view-modal';
      dialog.setAttribute('aria-label', 'Quick view');
      dialog.innerHTML = '<div class="modal__inner color-scheme-1"><button type="button" class="icon-button modal__close" data-modal-close aria-label="Close"><svg class="icon" width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M1 1l16 16M17 1L1 17"/></svg></button><div data-quick-view-content></div></div>';
      document.body.appendChild(dialog);
      this.dialog = dialog;
      return dialog;
    },
    async open(url, opener) {
      const dialog = this.ensure();
      const content = dialog.querySelector('[data-quick-view-content]');
      content.innerHTML = '<div class="modal__loading"></div>';
      opener?.classList.add('is-loading');
      dialog.showModal();
      lockScroll();
      try {
        const separator = url.includes('?') ? '&' : '?';
        const res = await fetch(`${url}${separator}section_id=quick-view`);
        const doc = parseHTML(await res.text());
        const qv = doc.querySelector('.quick-view');
        content.innerHTML = qv ? qv.outerHTML : '';
        if (window.Shopify?.PaymentButton) window.Shopify.PaymentButton.init();
      } catch (e) {
        content.innerHTML = '';
        toast(theme.strings.error);
      } finally {
        opener?.classList.remove('is-loading');
      }
    },
    close() {
      if (this.dialog?.open) this.dialog.close();
    }
  };

  /* ---------- Header ---------- */
  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      this.isTransparent = this.classList.contains('header--transparent');
      this.isSticky = this.dataset.sticky === 'true';

      const setHeight = () => {
        const h = this.offsetHeight;
        document.documentElement.style.setProperty('--header-height', `${h}px`);
        document.documentElement.style.setProperty('--header-sticky-offset', this.isSticky ? `${h}px` : '0px');
      };
      setHeight();
      if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(this);

      if (this.isTransparent) {
        const onScroll = () => this.classList.toggle('is-solid', window.scrollY > 40);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
      }

      // Touch devices: first tap opens dropdown
      this.querySelectorAll('[data-dropdown] > a').forEach((link) => {
        link.addEventListener('click', (event) => {
          if (!window.matchMedia('(hover: none)').matches) return;
          const item = link.parentElement;
          if (!item.classList.contains('is-open')) {
            event.preventDefault();
            this.querySelectorAll('[data-dropdown].is-open').forEach((el) => el.classList.remove('is-open'));
            item.classList.add('is-open');
            link.setAttribute('aria-expanded', 'true');
          }
        });
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) {
          this.querySelectorAll('[data-dropdown].is-open').forEach((el) => {
            el.classList.remove('is-open');
            el.querySelector('a')?.setAttribute('aria-expanded', 'false');
          });
        }
      });
      this.querySelectorAll('[data-dropdown]').forEach((item) => {
        const link = item.querySelector('a');
        item.addEventListener('mouseenter', () => link?.setAttribute('aria-expanded', 'true'));
        item.addEventListener('mouseleave', () => link?.setAttribute('aria-expanded', 'false'));
      });
    }
  }

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.announcement-bar__slide')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.querySelector('[data-prev]')?.addEventListener('click', () => { this.go(this.index - 1); this.restart(); });
      this.querySelector('[data-next]')?.addEventListener('click', () => { this.go(this.index + 1); this.restart(); });
      this.restart();
      this.addEventListener('shopify:block:select', (e) => {
        this.go(this.slides.indexOf(e.target));
        clearInterval(this.timer);
      });
    }

    go(i) {
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => s.classList.toggle('is-active', n === this.index));
    }

    restart() {
      clearInterval(this.timer);
      if (this.dataset.autoplay !== 'true') return;
      this.timer = setInterval(() => this.go(this.index + 1), Number(this.dataset.speed) || 5000);
    }

    disconnectedCallback() { clearInterval(this.timer); }
  }

  /* ---------- Slideshow (hero, testimonials) ---------- */
  class SlideShow extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.slideshow__slide')];
      this.dots = [...this.querySelectorAll('[data-go]')];
      this.index = Math.max(0, this.slides.findIndex((s) => s.classList.contains('is-active')));
      this.speed = Number(this.dataset.speed) || 6000;
      this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1;
      this.style.setProperty('--slide-duration', `${this.speed}ms`);
      if (!this.autoplay) this.dots.forEach((d) => d.classList.add('is-paused'));

      this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.go))));
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));

      // Swipe
      let startX = null;
      this.addEventListener('pointerdown', (e) => { startX = e.clientX; });
      this.addEventListener('pointerup', (e) => {
        if (startX === null) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 50) this.go(this.index + (dx < 0 ? 1 : -1));
        startX = null;
      });

      if (this.classList.contains('testimonials')) {
        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
      }

      document.addEventListener('visibilitychange', () => (document.hidden ? this.stop() : this.start()));

      this.addEventListener('shopify:block:select', (e) => {
        const i = this.slides.indexOf(e.target);
        if (i > -1) { this.go(i); this.stop(); this.paused = true; }
      });
      this.addEventListener('shopify:block:deselect', () => { this.paused = false; this.start(); });

      this.start();
    }

    go(i) {
      if (!this.slides.length) return;
      this.index = (i + this.slides.length) % this.slides.length;
      this.slides.forEach((s, n) => {
        const active = n === this.index;
        s.classList.toggle('is-active', active);
        s.setAttribute('aria-hidden', active ? 'false' : 'true');
      });
      this.dots.forEach((d) => d.classList.remove('is-active'));
      const dot = this.dots[this.index];
      if (dot) {
        void dot.offsetWidth;
        dot.classList.add('is-active');
      }
      this.start();
    }

    start() {
      clearTimeout(this.timer);
      if (!this.autoplay || this.paused) return;
      this.timer = setTimeout(() => this.go(this.index + 1), this.speed);
    }

    stop() { clearTimeout(this.timer); }

    disconnectedCallback() { this.stop(); }
  }

  /* ---------- Product slider (carousel) ---------- */
  class ProductSlider extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slider-track]');
      this.prev = this.querySelector(':scope > .product-slider__nav [data-prev]');
      this.next = this.querySelector(':scope > .product-slider__nav [data-next]');
      if (!this.track || !this.prev) return;
      this.prev.addEventListener('click', () => this.scroll(-1));
      this.next.addEventListener('click', () => this.scroll(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(() => this.update()).observe(this.track);
      this.update();
    }

    scroll(dir) {
      this.track.scrollBy({ left: dir * this.track.clientWidth, behavior: 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      this.prev.disabled = this.track.scrollLeft <= 2;
      this.next.disabled = this.track.scrollLeft >= max;
      this.prev.parentElement.classList.toggle('is-hidden', max <= 0);
    }
  }

  /* ---------- Tabs ---------- */
  class TabsComponent extends HTMLElement {
    connectedCallback() {
      this.buttons = [...this.querySelectorAll('[data-tab]')];
      this.panels = [...this.querySelectorAll('[data-panel]')];
      this.buttons.forEach((btn) => btn.addEventListener('click', () => this.select(Number(btn.dataset.tab))));
      this.addEventListener('shopify:block:select', (e) => {
        const i = this.panels.indexOf(e.target);
        if (i > -1) this.select(i);
      });
    }

    select(i) {
      this.buttons.forEach((b, n) => {
        b.classList.toggle('is-active', n === i);
        b.setAttribute('aria-selected', n === i ? 'true' : 'false');
      });
      this.panels.forEach((p, n) => {
        p.hidden = n !== i;
        p.classList.toggle('is-active', n === i);
        if (n === i) initReveal(p);
      });
    }
  }

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((btn) => btn.addEventListener('click', (e) => {
        e.preventDefault();
        const min = Number(this.input.min) || 0;
        const current = parseInt(this.input.value, 10) || 0;
        const next = btn.name === 'plus' ? current + 1 : Math.max(min, current - 1);
        if (next === current) return;
        this.input.value = next;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      }));
    }
  }

  /* ---------- Variant picker ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.addEventListener('change', () => this.onChange());
    }

    selectedOptions() {
      return [...this.querySelectorAll('.variant-picker__option')].map((opt) => {
        const select = opt.querySelector('select');
        if (select) return select.value;
        const checked = opt.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    onChange() {
      const options = this.selectedOptions();
      this.querySelectorAll('fieldset.variant-picker__option').forEach((fs) => {
        const checked = fs.querySelector('input:checked');
        const label = fs.querySelector('[data-selected-value]');
        if (checked && label) label.textContent = checked.value;
      });

      const variant = this.variants.find((v) => v.options.every((o, i) => o === options[i]));
      const container = this.closest('[data-product-section]') || document;
      const form = document.getElementById(this.dataset.form);
      const idInput = form?.querySelector('input[name="id"]');
      const button = container.querySelector('[data-update="add-button"]');

      if (!variant) {
        if (button) {
          button.disabled = true;
          button.innerHTML = `<span>${theme.strings.unavailable}</span>`;
        }
        if (idInput) idInput.disabled = true;
        return;
      }

      if (idInput) { idInput.value = variant.id; idInput.disabled = false; }
      if (!this.hasAttribute('data-no-history') && container !== document && container.closest('.main') && !container.closest('dialog')) {
        window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
      }

      if (variant.featured_media) {
        container.querySelector('product-gallery')?.goToMedia(variant.featured_media.id);
      }

      const sectionId = container.dataset?.renderSection || container.dataset?.sectionId || this.dataset.section;
      this.controller?.abort();
      this.controller = new AbortController();
      fetch(`${this.dataset.url}?variant=${variant.id}&section_id=${sectionId}`, { signal: this.controller.signal })
        .then((res) => res.text())
        .then((html) => {
          const doc = parseHTML(html);
          container.querySelectorAll('[data-update]').forEach((el) => {
            const src = doc.querySelector(`[data-update="${el.dataset.update}"]`);
            if (!src) return;
            if (el.dataset.update === 'add-button') {
              el.disabled = src.disabled;
              el.innerHTML = src.innerHTML;
            } else {
              el.innerHTML = src.innerHTML;
            }
          });
          const freshLabels = doc.querySelectorAll('variant-picker .option-button');
          const labels = this.querySelectorAll('.option-button');
          if (freshLabels.length === labels.length) {
            labels.forEach((l, i) => l.classList.toggle('is-unavailable', freshLabels[i].classList.contains('is-unavailable')));
          }
          const installments = container.querySelector('form[id^="installments-"] input[name="id"]');
          if (installments) installments.value = variant.id;
        })
        .catch(() => {});
    }
  }

  /* ---------- Product gallery ---------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      if (!this.track) return;
      this.items = [...this.track.children];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.counter = this.querySelector('[data-gallery-current]');
      this.index = Math.max(0, this.items.findIndex((i) => i.classList.contains('is-active')));

      this.thumbs.forEach((t) => t.addEventListener('click', () => this.goTo(Number(t.dataset.thumb))));
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.track.addEventListener('scroll', debounce(() => this.syncFromScroll(), 80), { passive: true });

      if (this.index > 0 && this.isHorizontal()) {
        this.track.style.scrollBehavior = 'auto';
        this.track.scrollLeft = this.items[this.index].offsetLeft;
        this.track.style.scrollBehavior = '';
      }

      if (this.hasAttribute('data-zoom')) this.initZoom();
    }

    isHorizontal() {
      return this.track.scrollWidth > this.track.clientWidth + 2;
    }

    goTo(i) {
      if (!this.items.length) return;
      this.index = (i + this.items.length) % this.items.length;
      const item = this.items[this.index];
      if (this.isHorizontal()) {
        this.track.scrollTo({ left: item.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
      }
      this.setActive();
    }

    goToMedia(id) {
      const i = this.items.findIndex((item) => item.dataset.mediaId === String(id));
      if (i < 0) return;
      if (this.isHorizontal()) this.goTo(i);
      else if (i > 0) {
        this.track.prepend(this.items[i]);
        this.items = [...this.track.children];
        this.index = 0;
        this.setActive();
      }
    }

    syncFromScroll() {
      if (!this.isHorizontal()) return;
      const i = Math.round(this.track.scrollLeft / this.track.clientWidth);
      if (i !== this.index && this.items[i]) {
        this.index = i;
        this.setActive();
      }
    }

    setActive() {
      this.items.forEach((item, n) => {
        item.classList.toggle('is-active', n === this.index);
        if (n !== this.index) item.querySelectorAll('video').forEach((v) => v.pause());
      });
      this.thumbs.forEach((t, n) => t.classList.toggle('is-active', n === this.index));
      if (this.counter) this.counter.textContent = this.index + 1;
    }

    initZoom() {
      const dialog = this.querySelector('[data-zoom-dialog]');
      if (!dialog) return;
      this.querySelectorAll('[data-zoom-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const index = trigger.closest('[data-index]')?.dataset.index;
          dialog.showModal();
          lockScroll();
          const img = dialog.querySelector(`img[data-index="${index}"]`);
          if (img) requestAnimationFrame(() => img.scrollIntoView({ block: 'start' }));
        });
      });
      dialog.addEventListener('click', (e) => {
        if (e.target.closest('[data-zoom-close]') || e.target.tagName === 'IMG') dialog.close();
      });
    }
  }

  /* ---------- Product recommendations ---------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const load = () => {
        fetch(this.dataset.url)
          .then((res) => res.text())
          .then((html) => {
            const fresh = parseHTML(html).querySelector('product-recommendations');
            if (fresh && fresh.innerHTML.trim().length) {
              this.innerHTML = fresh.innerHTML;
              initReveal(this);
            }
          })
          .catch(() => {});
      };
      if (!('IntersectionObserver' in window)) return load();
      const io = new IntersectionObserver((entries) => {
        if (!entries[0].isIntersecting) return;
        io.disconnect();
        load();
      }, { rootMargin: '0px 0px 400px 0px' });
      io.observe(this);
    }
  }

  /* ---------- Video ---------- */
  class VideoSection extends HTMLElement {
    connectedCallback() {
      this.querySelector('[data-video-play]')?.addEventListener('click', () => {
        const template = this.querySelector('[data-video-template]');
        if (!template) return;
        this.appendChild(template.content.cloneNode(true));
        this.classList.add('is-playing');
        this.querySelector('video')?.play();
      });
    }
  }

  class ModalOpener extends HTMLElement {}

  /* ---------- Register elements ---------- */
  const define = (name, ctor) => { if (!customElements.get(name)) customElements.define(name, ctor); };
  define('menu-drawer', MenuDrawer);
  define('facet-drawer', FacetDrawer);
  define('cart-drawer', CartDrawer);
  define('search-drawer', SearchDrawer);
  define('header-component', HeaderComponent);
  define('announcement-bar', AnnouncementBar);
  define('slide-show', SlideShow);
  define('product-slider', ProductSlider);
  define('tabs-component', TabsComponent);
  define('quantity-input', QuantityInput);
  define('variant-picker', VariantPicker);
  define('product-gallery', ProductGallery);
  define('product-recommendations', ProductRecommendations);
  define('video-section', VideoSection);
  define('modal-opener', ModalOpener);

  /* ---------- Parallax ---------- */
  const initParallax = () => {
    const items = document.querySelectorAll('.image-overlay--parallax');
    if (!items.length || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const update = () => {
      items.forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        const progress = (rect.top + rect.height) / (window.innerHeight + rect.height);
        const img = el.querySelector('.slideshow__image');
        if (img) img.style.transform = `translateY(${(progress - 1) * 16}%)`;
      });
    };
    window.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    update();
  };

  /* ---------- Init ---------- */
  const init = () => {
    initReveal();
    initParallax();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* ---------- Theme editor ---------- */
  document.addEventListener('shopify:section:load', (e) => {
    initReveal(e.target);
    initParallax();
  });
  document.addEventListener('shopify:section:select', (e) => {
    const drawer = e.target.querySelector('cart-drawer');
    if (drawer) drawer.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    const drawer = e.target.querySelector('cart-drawer');
    if (drawer) drawer.close(true);
  });
})();
